/* eslint-disable react/prop-types */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { submitPropertyTourRegistration } from '@/api/contactUs';
import { trackButtonClick } from '@/utils/analytics';
import './register-tour-modal.css';

/**
 * RegisterTourModal
 *
 * Modal for capturing user details to register for a commercial property tour.
 * First name and last name are separate editable fields pre-populated from the user profile.
 * Email, phone, and property address are pre-populated and read-only.
 * On successful submission, shows a success message and auto-closes after 2 seconds.
 *
 * Props:
 *   show          {boolean}  — controls modal visibility
 *   onHide        {function} — called when the modal should close
 *   onSuccess     {function} — called after successful submission (2s delay); parent uses this to hide the Register button
 *   user          {object}   — Redux user object (userFirstName, userLastName, userEmailId, primaryPhone)
 *   propertyId    {string}   — property ID
 *   address       {object}   — address object from pdpInitData.propertyDetails.address
 */
const RegisterTourModal = ({ show, onHide, onSuccess, user, propertyId, address }) => {
  const triggerRef = useRef(null);
  const firstNameInputRef = useRef(null);

  const initialFirstName = user?.userFirstName || '';
  const initialLastName = user?.userLastName || '';
  const email = user?.userEmailId || '';
  const phone = user?.primaryPhone || '';

  const formattedAddress = address
    ? [
        [address.streetNumber, address.streetName].filter(Boolean).join(' '),
        address.city,
        [address.state, address.zip].filter(Boolean).join(' '),
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'success' | 'error'
  const [submitError, setSubmitError] = useState('');

  // Re-sync fields if user profile changes while modal is open
  useEffect(() => {
    if (show) {
      setFirstName(initialFirstName);
      setLastName(initialLastName);
      setFirstNameError('');
      setLastNameError('');
      setSubmitStatus(null);
      setSubmitError('');
    }
  }, [show, initialFirstName, initialLastName]);

  // Auto-close and notify parent after successful submission
  useEffect(() => {
    if (submitStatus === 'success') {
      const timer = setTimeout(() => {
        onSuccess?.();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus, onSuccess]);

  // Save trigger element and move focus to first name on open
  const handleShow = useCallback(() => {
    triggerRef.current = document.activeElement;
    setTimeout(() => firstNameInputRef.current?.focus(), 50);
  }, []);

  const handleExited = useCallback(() => {
    setFirstName(initialFirstName);
    setLastName(initialLastName);
    setFirstNameError('');
    setLastNameError('');
    setSubmitStatus(null);
    setSubmitError('');
    setIsSubmitting(false);
    if (triggerRef.current) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [initialFirstName, initialLastName]);

  const validateFirstName = () => {
    if (!firstName.trim()) {
      setFirstNameError('First name is required.');
      return false;
    }
    if (firstName !== firstName.trim()) {
      setFirstNameError('First name cannot start or end with spaces.');
      return false;
    }
    if (!/^[a-zA-Z0-9\-.'_]+(\s[a-zA-Z0-9\-.'_]+)*$/.test(firstName.trim())) {
      setFirstNameError("Characters allowed: a-z A-Z 0-9 and - . ' _");
      return false;
    }
    if (firstName.trim().length > 40) {
      setFirstNameError('First name cannot exceed 40 characters.');
      return false;
    }
    setFirstNameError('');
    return true;
  };

  const validateLastName = () => {
    if (!lastName.trim()) {
      setLastNameError('Last name is required.');
      return false;
    }
    if (lastName !== lastName.trim()) {
      setLastNameError('Last name cannot start or end with spaces.');
      return false;
    }
    if (!/^[a-zA-Z0-9\-.'_]+(\s[a-zA-Z0-9\-.'_]+)*$/.test(lastName.trim())) {
      setLastNameError("Characters allowed: a-z A-Z 0-9 and - . ' _");
      return false;
    }
    if (lastName.trim().length > 40) {
      setLastNameError('Last name cannot exceed 40 characters.');
      return false;
    }
    setLastNameError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const firstNameValid = validateFirstName();
    const lastNameValid = validateLastName();
    if (!firstNameValid || !lastNameValid) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitError('');

    trackButtonClick({
      pageName: 'Register for Property Tour - Submitted',
      eventCategory: 'COMMERCIAL-PDP',
      eventSource: 'on form submit',
    });

    try {
      const response = await submitPropertyTourRegistration({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email,
        phone,
        propertyId,
        propertyAddress: formattedAddress,
      });

      if (response?.status === 'SUCCESS') {
        setSubmitStatus('success');
      } else {
        setSubmitError(response?.errorMessage || 'Something went wrong. Please try again.');
        setSubmitStatus('error');
      }
    } catch {
      setSubmitError('Something went wrong. Please try again.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled = isSubmitting || submitStatus === 'success';

  return (
    <Modal
      show={show}
      onHide={submitStatus === 'success' ? onSuccess : onHide}
      size="md"
      centered
      dialogClassName="register-tour-modal"
      aria-labelledby="register-tour-modal-title"
      enforceFocus
      restoreFocus
      onShow={handleShow}
      onExited={handleExited}
    >
      <div className="register-tour-modal__header">
        <h3 className="register-tour-modal__header-title" id="register-tour-modal-title">
          Register for Property Tour
        </h3>
        <button
          type="button"
          className="register-tour-modal__close-btn"
          onClick={submitStatus === 'success' ? onSuccess : onHide}
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      <Modal.Body className="register-tour-modal__body">
        {/* Success state — hide the form entirely */}
        {submitStatus === 'success' ? (
          <div
            className="register-tour-modal__feedback register-tour-modal__feedback--success register-tour-modal__feedback--success-only"
            role="alert"
          >
            <span className="register-tour-modal__feedback-icon" aria-hidden="true">✓</span>
           You are registered for the property tour.
          </div>
        ) : (
        <>
        {submitStatus === 'error' && (
          <div
            className="register-tour-modal__feedback register-tour-modal__feedback--error"
            role="alert"
          >
            <span className="register-tour-modal__feedback-icon" aria-hidden="true">!</span>
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* First Name and Last Name — side by side */}
          <div className="register-tour-modal__name-row">
            <div className="register-tour-modal__field">
              <label
                htmlFor="rtml-first-name"
                className="register-tour-modal__label register-tour-modal__label--required"
              >
                First Name
              </label>
              <input
                id="rtml-first-name"
                ref={firstNameInputRef}
                type="text"
                className={`register-tour-modal__input${firstNameError ? ' register-tour-modal__input--error' : ''}`}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onBlur={validateFirstName}
                aria-required="true"
                aria-describedby={firstNameError ? 'rtml-first-name-error' : undefined}
                aria-invalid={!!firstNameError}
                disabled={isDisabled}
              />
              {firstNameError && (
                <div id="rtml-first-name-error" className="register-tour-modal__field-error" role="alert">
                  {firstNameError}
                </div>
              )}
            </div>

            <div className="register-tour-modal__field">
              <label
                htmlFor="rtml-last-name"
                className="register-tour-modal__label register-tour-modal__label--required"
              >
                Last Name
              </label>
              <input
                id="rtml-last-name"
                type="text"
                className={`register-tour-modal__input${lastNameError ? ' register-tour-modal__input--error' : ''}`}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onBlur={validateLastName}
                aria-required="true"
                aria-describedby={lastNameError ? 'rtml-last-name-error' : undefined}
                aria-invalid={!!lastNameError}
                disabled={isDisabled}
              />
              {lastNameError && (
                <div id="rtml-last-name-error" className="register-tour-modal__field-error" role="alert">
                  {lastNameError}
                </div>
              )}
            </div>
          </div>

          {/* Email — read-only */}
          <div className="register-tour-modal__field">
            <label htmlFor="rtml-email" className="register-tour-modal__label">
              Email
            </label>
            <input
              id="rtml-email"
              type="email"
              className="register-tour-modal__input register-tour-modal__input--readonly"
              value={email}
              readOnly
              aria-readonly="true"
              tabIndex={-1}
            />
          </div>

          {/* Phone — read-only */}
          <div className="register-tour-modal__field">
            <label htmlFor="rtml-phone" className="register-tour-modal__label">
              Phone Number
            </label>
            <input
              id="rtml-phone"
              type="tel"
              className="register-tour-modal__input register-tour-modal__input--readonly"
              value={phone}
              readOnly
              aria-readonly="true"
              tabIndex={-1}
            />
          </div>

          {/* Property Address — read-only */}
          <div className="register-tour-modal__field">
            <label htmlFor="rtml-address" className="register-tour-modal__label">
              Property Address
            </label>
            <input
              id="rtml-address"
              type="text"
              className="register-tour-modal__input register-tour-modal__input--readonly"
              value={formattedAddress}
              readOnly
              aria-readonly="true"
              tabIndex={-1}
            />
          </div>

          {/* Actions */}
          <div className="register-tour-modal__footer">
            <button
              type="button"
              className="register-tour-modal__cancel-btn"
              onClick={onHide}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="register-tour-modal__submit-btn"
              disabled={isDisabled}
            >
              {isSubmitting && <span className="register-tour-modal__spinner" aria-hidden="true" />}
              {isSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </form>
        </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default RegisterTourModal;
